import React, { useState } from "react";
import { createDesign } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Loader2, Plus, MapPin, Upload } from "lucide-react";
import { parseAddressString } from "./ViewOptionsPanel";

const STYLES = ["Modern", "Farmhouse", "Colonial", "Craftsman", "Mountain Cabin", "Contemporary", "Cape Cod"];

export default function NewProjectDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [style, setStyle] = useState("Modern");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zip, setZip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState("chat"); // 'chat' | 'upload'

  if (!open) return null;

  const buildLabel = () => {
    const parts = [city, stateCode, zip].map((s) => (s || "").trim()).filter(Boolean);
    return parts.length ? parts.join(", ").replace(/,\s*(\d{5})$/, " $1") : null;
  };

  const handleAddressPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    const parsed = parseAddressString(text);
    if (parsed) {
      e.preventDefault();
      if (parsed.city) setCity(parsed.city);
      if (parsed.state) setStateCode(parsed.state);
      if (parsed.zip) setZip(parsed.zip);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim() || undefined,
        style,
      };
      const label = buildLabel();
      if (city || stateCode || zip) {
        payload.build_site_metadata = {
          city: city.trim() || undefined,
          state: stateCode.trim() || undefined,
          zip_code: zip.trim() || undefined,
          country: "US",
          label,
        };
      }
      const r = await createDesign(payload);
      toast.success("Project created");
      const dst = submitMode === "upload"
        ? `/designs/${r.data.design_id}?upload=1`
        : `/designs/${r.data.design_id}`;
      navigate(dst);
    } catch (err) {
      toast.error("Could not create project");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="new-project-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg bg-zinc-950 border border-brass-500/40 shadow-[0_12px_64px_rgba(196,169,107,0.12)]"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
              // New Project
            </div>
            <h2 className="font-display text-xl text-zinc-50">Start a new home</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-100 p-1"
            aria-label="Close"
            data-testid="new-project-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-6 space-y-5">
          <div>
            <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1.5">
              Project Name (optional)
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Lakeview Cottage"
              data-testid="np-name"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-3 py-2.5 font-mono text-sm text-zinc-100"
            />
          </div>
          <div>
            <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1.5">
              Style
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="np-style">
              {STYLES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStyle(s)}
                  className={
                    "font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors " +
                    (style === s
                      ? "border-brass-500 bg-brass-500 text-zinc-950"
                      : "border-zinc-800 text-zinc-300 hover:border-brass-500/60")
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <fieldset className="border border-zinc-800 p-4">
            <legend className="font-mono text-[9px] uppercase tracking-widest text-brass-400 px-2 inline-flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> // Build Location (optional)
            </legend>
            <div className="grid grid-cols-12 gap-2">
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onPaste={handleAddressPaste}
                placeholder="City — or paste full address"
                data-testid="np-city"
                className="col-span-7 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-3 py-2 font-mono text-sm text-zinc-100"
              />
              <input
                value={stateCode}
                onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
                onPaste={handleAddressPaste}
                placeholder="ST"
                maxLength={2}
                data-testid="np-state"
                className="col-span-2 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2 py-2 font-mono text-sm text-zinc-100 text-center"
              />
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                onPaste={handleAddressPaste}
                placeholder="ZIP"
                inputMode="numeric"
                data-testid="np-zip"
                className="col-span-3 bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-2 py-2 font-mono text-sm text-zinc-100"
              />
            </div>
            <p className="font-mono text-[9px] text-zinc-500 mt-2 leading-relaxed">
              Powers climate-aware defaults — solar orientation, materials, pitch.
            </p>
          </fieldset>
        </div>
        <footer className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-800 bg-zinc-900/40 flex-wrap">
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100 px-4 py-2"
            data-testid="np-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={() => setSubmitMode("upload")}
            disabled={submitting}
            data-testid="np-submit-upload"
            title="Create the project and jump straight into the upload-a-floor-plan flow"
            className="inline-flex items-center gap-2 border border-brass-500/60 bg-brass-950/20 hover:bg-brass-900/40 disabled:opacity-50 text-brass-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2 transition-colors"
          >
            {submitting && submitMode === "upload" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Start from file
          </button>
          <button
            type="submit"
            onClick={() => setSubmitMode("chat")}
            disabled={submitting}
            data-testid="np-submit"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2 transition-colors"
          >
            {submitting && submitMode === "chat" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create Project
          </button>
        </footer>
      </form>
    </div>
  );
}
